import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { memoObjectKeys } from '../api/memoObjectAPI';
import { fetchMemoObject } from '../api/memoObjectAPI';
import { useNotificationProcessingState } from '../managers/useNotificationProcessingState';

/**
 * Memo Object查询hooks - 统一命名规范
 */

// Memo Object查询
export const useMemoObjectQuery = (memoId: string, options?: { enabled?: boolean }) => {
  // 通知消息处理期间，避免自动触发object查询，由消息处理函数统一控制
  const isNotificationProcessing = useNotificationProcessingState((state) => state.isProcessing);
  
  const queryResult = useQuery({
    queryKey: memoObjectKeys.memoObjects(memoId),
    queryFn: async () => {
      try {
        // 调用API获取memo object数据
        const memoObjectData = await fetchMemoObject(memoId);
        return memoObjectData || null;
      } catch (error) {
        throw error;
      }
    },
    enabled: (options?.enabled !== undefined ? options.enabled : !!memoId) && !isNotificationProcessing,
    staleTime: 5 * 60 * 1000,
    // 添加缓存优先策略：完全禁用所有自动重新获取
    refetchOnMount: false, // 组件挂载时不自动重新获取
    refetchOnWindowFocus: false, // 窗口聚焦时不自动重新获取
    refetchOnReconnect: false, // 网络重连时不自动重新获取
    gcTime: 10 * 60 * 1000, // 延长垃圾回收时间
  });

  return queryResult;
};

/**
 * Memo Object操作mutation hooks - 统一命名规范
 */

// Memo Object创建mutation (已简化，API方法已删除)
export const useCreateMemoObjectMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ memoId, objects }: { memoId: string; objects: any[] }) => {
      // API方法已删除，返回成功状态
      return Promise.resolve({ success: true });
    },
    onSuccess: (data, variables) => {
      // 使相关查询失效
      queryClient.invalidateQueries({ queryKey: memoObjectKeys.memoObjects(variables.memoId) });
    },
  });
};

// Memo Object更新mutation (已简化，API方法已删除)
export const useUpdateMemoObjectMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ memoId, objects }: { memoId: string; objects: any[] }) => {
      // API方法已删除，返回成功状态
      return Promise.resolve({ success: true });
    },
    onSuccess: (data, variables) => {
      // 使相关查询失效
      queryClient.invalidateQueries({ queryKey: memoObjectKeys.memoObjects(variables.memoId) });
    },
  });
};